declare global {
    interface Window {
        gtag: (...args: any[]) => void;
        fbq: (...args: any[]) => void;
        _fbq: any;
    }
}

export interface ConversionData {
    value?: number;
    currency?: string;
    transaction_id?: string;
    [key: string]: any;
}

export const useAdsTracking = () => {
    // Google Ads tracking
    const initGoogleAds = (conversionId: string) => {
        if (typeof window === "undefined") return;

        // Load Google Ads script
        const script = document.createElement("script");
        script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
        script.async = true;
        document.head.appendChild(script);

        // Initialize gtag
        window.gtag =
            window.gtag ||
            function () {
                (window.gtag as any).q = (window.gtag as any).q || [];
                (window.gtag as any).q.push(arguments);
            };
        window.gtag("js", new Date());
        window.gtag("config", conversionId);
    };

    const trackGoogleAdsConversion = (
        conversionLabel: string,
        data?: ConversionData,
    ) => {
        if (!window.gtag) return;

        window.gtag("event", "conversion", {
            send_to: conversionLabel,
            value: data?.value,
            currency: data?.currency || "CZK",
            transaction_id: data?.transaction_id,
            ...data,
        });
    };

    // Meta (Facebook) Ads tracking
    const initMetaAds = (pixelId: string) => {
        if (typeof window === "undefined") return; // Facebook Pixel base code
        (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod
                    ? n.callMethod.apply(n, arguments)
                    : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = "2.0";
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(
            window,
            document,
            "script",
            "https://connect.facebook.net/en_US/fbevents.js",
        );

        window.fbq("init", pixelId);
    };

    const trackMetaConversion = (
        eventName: string,
        data?: ConversionData,
        eventID?: string,
    ) => {
        if (!window.fbq) return;

        const payload = {
            value: data?.value,
            currency: data?.currency || "CZK",
            source: "app",
            ...data,
        };

        if (eventID) {
            window.fbq("track", eventName, payload, { eventID });
        } else {
            window.fbq("track", eventName, payload);
        }
    };

    // Combined conversion tracking functions
    const trackSignupConversion = (
        googleConversionLabel?: string,
        value?: number,
        transactionId?: string,
        userId?: string,
    ) => {
        const conversionData: ConversionData = {
            value,
            currency: "CZK",
            transaction_id: transactionId,
        };

        // Track Google Ads conversion
        if (googleConversionLabel) {
            trackGoogleAdsConversion(googleConversionLabel, conversionData);
        }

        // Track Meta conversion (deduplicated with backend CAPI via event_id)
        const eventID = userId ? `signup_${userId}` : undefined;
        trackMetaConversion("CompleteRegistration", conversionData, eventID);
    };

    const trackSubscriptionConversion = (
        googleConversionLabel?: string,
        value?: number,
        plan?: string,
        transactionId?: string,
        teamId?: string,
    ) => {
        const conversionData: ConversionData = {
            value,
            currency: "CZK",
            transaction_id: transactionId,
            subscription_plan: plan,
        };

        // Track Google Ads conversion
        if (googleConversionLabel) {
            trackGoogleAdsConversion(googleConversionLabel, conversionData);
        }

        // Track Meta conversion (deduplicated with backend CAPI via event_id)
        const eventID =
            teamId && plan ? `purchase_team_${teamId}_plan_${plan}` : undefined;
        trackMetaConversion("Purchase", conversionData, eventID);
    };

    const trackTrialStartConversion = (
        value?: number,
        transactionId?: string,
    ) => {
        const conversionData: ConversionData = {
            value,
            currency: "CZK",
            transaction_id: transactionId,
        };

        // Track Meta conversion for trial start
        trackMetaConversion("StartTrial", conversionData);
    };

    return {
        initGoogleAds,
        trackGoogleAdsConversion,
        initMetaAds,
        trackMetaConversion,
        trackSignupConversion,
        trackSubscriptionConversion,
        trackTrialStartConversion,
    };
};
